import _ from 'lodash'
import { Link2 } from 'lucide-react'
import { useState } from 'react'
import { Card } from '#app/components/ui/card.js'
import { Input } from '#app/components/ui/input.js'
import {
	Select,
	SelectItem,
	SelectContent,
	SelectValue,
	SelectTrigger,
} from '#app/components/ui/select.js'
import { Switch } from '#app/components/ui/switch.js'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.js'

export const GlobalInputSettings = ({
	node,
	setRequired,
	setConnect,
	setRegex,
	setUnique,
	canChangeConnect = true,
	canChangeRequired = true,
	canChangeRegex = false,
	canBeUnique = false,
}: {
	node: any
	setRequired: (e: any) => void
	setConnect: (value: string) => void
	setRegex?: (value: string) => void
	setUnique?: (value: boolean) => void
	canChangeConnect?: boolean
	canChangeRequired?: boolean
	canChangeRegex?: boolean
	canBeUnique?: boolean
}) => {
	const [regexError, setRegexError] = useState<string | null>(null)

	return (
		<>
			<Card
				variant="filled"
				className="items-center justify-between gap-1"
				size="sm"
			>
				<p className="font-medium">Required field</p>
				<Switch
					checked={node.attrs.required}
					onCheckedChange={setRequired}
					disabled={!canChangeRequired}
				/>
			</Card>
			<Card
				variant="filled"
				className="items-center justify-between gap-1"
				size="sm"
			>
				<p className="font-medium">Unique field</p>
				<Switch
					defaultChecked={node.attrs.unique}
					onCheckedChange={setUnique}
					disabled={!canBeUnique}
				/>
			</Card>
			<Card variant="filled" className="flex flex-col gap-1" size="sm">
				<p className="font-medium">Regular expression</p>
				<p className="text-sm text-muted-foreground">
					Filter values allowed to be entered in the input.
				</p>
				<Input
					defaultValue={node.attrs.regex}
					variant="s"
					onChange={e => {
						const regex = new RegExp(e.target.value)
						if (regex.source !== e.target.value) {
							setRegexError('Invalid regular expression')
						} else {
							setRegexError(null)
							setRegex?.(e.target.value)
						}
					}}
					disabled={!canChangeRegex}
					className={regexError ? 'border-red-500' : ''}
				/>
				{regexError && <p className="text-sm text-red-500">{regexError}</p>}
			</Card>
			<Card variant="filled" className="flex-col gap-1" size="sm">
				<div className="flex w-full flex-col gap-2">
					<div className="flex justify-between">
						<p className="flex items-center gap-1 font-medium">Connect to</p>
						<span className="flex items-center">
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger>
										<Link2 width={16} />
									</TooltipTrigger>
									<TooltipContent>
										<p>
											Connect this input to an existing people data field. If
											none is selected this will create a new data field named
											{node.attrs.name}
										</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</span>
					</div>
					<p className="text-sm text-muted-foreground">
						Connect this input to an existing people data field. If none is
						selected this will create a new data field named{' '}
						<span className="font-medium">{_.startCase(node.attrs.name)}</span>
					</p>
					<Select
						defaultValue={node.attrs.to}
						onValueChange={setConnect}
						disabled={!canChangeConnect}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Existing data" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="none">-</SelectItem>
							<SelectItem value="email">Email</SelectItem>
							<SelectItem value="firstName">First name</SelectItem>
							<SelectItem value="lastName">Last name</SelectItem>
							<SelectItem value="phoneNumber">Phone number</SelectItem>
							<SelectItem value="company">Company</SelectItem>
							<SelectItem value="address">Address</SelectItem>
							<SelectItem value="wallet">Wallet</SelectItem>
							<SelectItem value="website">Website</SelectItem>
							<SelectItem value="instagram">Instagram</SelectItem>
							<SelectItem value="twitter">Twitter</SelectItem>
							<SelectItem value="country">Country</SelectItem>
							<SelectItem value="city">City</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</Card>
		</>
	)
}
